/*
  Slippage — how a piece of work stands against the date it is committed to.

  For work still in flight:

      slippage = sysdate - NVL(revised_eta, eta)

  in whole days. Positive is days PAST the target, negative is days still to
  run, and null means there is no target date at all — which is emphatically not
  zero, and must never sort as "on time". DataTable's sort treats null as blank
  and parks it last in both directions, which is why null is the right answer
  here rather than 0 or Infinity.

  NVL, i.e. the revised date wins when there is one: the revised ETA is the date
  the work is now committed to, so it is the honest thing to measure against
  day to day. Asking the OTHER question — did it blow through its original
  promise — is what matchesSchedule's "overdue_revised" is for, and that one
  deliberately measures against `eta` only.

  FOR WORK ALREADY FINISHED, THE LIVE FORMULA IS MEANINGLESS. A task shipped in
  June is not "78 days late and counting" — it stopped accruing the day it was
  delivered. 127 of the 156 finished tasks in this database sit past their
  target date, so left alone they dominate every overdue count and every sort
  with a number that grows forever about work nobody is waiting on. Finished
  rows therefore report DELIVERED VARIANCE instead:

      variance = completed_at - NVL(revised_eta, eta)

  which is a fixed historical fact: shipped three days late, or two days early.

  Projects and tasks differ in what they have to work with:
    * a task has `eta` and `completed_at`, so both numbers are available;
    * a project's original commitment is `end_date` (there is no projects.eta
      column — assuming one existed silently broke the weekly digest once) and
      it has NO completion timestamp at all, so a closed project can only be
      reported as done, never as delivered-early-or-late.
*/

const MS_PER_DAY = 86400000;

/* Which statuses mean "this is finished, stop the clock". */
export const DONE_TASK_STATUSES = ["live", "closed", "cancelled"];
export const DONE_PROJECT_STATUSES = ["closed"];

/*
  A date's position on the LOCAL calendar, as a whole-day integer.

  Two traps, both live here so no caller has to remember them:

  1. `new Date("2026-09-04")` parses a BARE date as UTC midnight. Anywhere west
     of Greenwich that instant belongs to the 3rd, so the day would come back
     one early. Bare dates therefore have their parts read off the string and
     are never handed to Date at all.
  2. A real timestamptz is an instant, so its calendar day depends on the
     reader's timezone. It is converted using LOCAL fields, which is what the
     rest of the app already shows via toLocaleDateString — the number in this
     column and the date next to it must agree.
*/
const localDayIndex = (value) => {
  if (!value) return null;
  const s = String(value);

  const bare = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (bare) return Math.floor(Date.UTC(+bare[1], +bare[2] - 1, +bare[3]) / MS_PER_DAY);

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / MS_PER_DAY);
};

const todayIndex = () => {
  const n = new Date();
  return Math.floor(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate()) / MS_PER_DAY);
};

/** NVL(revised, eta) — the date the work is actually being held to. */
export const targetDate = (revised, eta) => revised || eta || null;

/** sysdate - NVL(revised, eta), in whole days. null when there is no target. */
export function ageingDays(revised, eta) {
  const t = localDayIndex(targetDate(revised, eta));
  if (t === null) return null;
  return todayIndex() - t;
}

/** Days past `eta` alone, ignoring any revision. null when there is no eta. */
export function ageingAgainstOriginal(eta) {
  const t = localDayIndex(eta);
  if (t === null) return null;
  return todayIndex() - t;
}

/**
 * completed_at - NVL(revised, eta): how late (or early) it actually landed.
 * A fixed fact, unlike ageing. null if either date is missing.
 */
export function deliveredVariance(completedAt, revised, eta) {
  const c = localDayIndex(completedAt);
  const t = localDayIndex(targetDate(revised, eta));
  if (c === null || t === null) return null;
  return c - t;
}

/*
  THE TWO NUMBERS THE COLUMN ACTUALLY DISPLAYS.

  Both are the NEGATION of the functions above, and the negation is the point:
  ageing counts UP as things get worse, which is the right basis for a formula
  and the wrong one for a reader. The column shows the schedule the way people
  describe it — "+5d" is five days of headroom left, "-22d" is twenty-two days
  consumed past the promise. Positive is comfortable, negative is trouble, in
  both cases.

  The formulae, spelled out:

      days left        =  NVL(revised_eta, eta) - sysdate
      delivered early  =  NVL(revised_eta, eta) - completed_at

  so a live task showing -3d took three days longer than it promised, and one
  showing +2d landed two days early.
*/

/** NVL(revised, eta) - sysdate. Positive = time still to run. */
export const daysLeft = (revised, eta) => {
  const a = ageingDays(revised, eta);
  return a === null ? null : -a;
};

/** NVL(revised, eta) - completed_at. Positive = delivered early. */
export const daysEarly = (completedAt, revised, eta) => {
  const v = deliveredVariance(completedAt, revised, eta);
  return v === null ? null : -v;
};

/**
 * What the Slippage column sorts on.
 *
 * Returns the SAME quantity the cell shows (days left), so the arrow never
 * contradicts the column: the grid's first click is ascending, which puts the
 * most negative — the worst overrun — at the top. Sorting on ageing while
 * displaying days-left would rank correctly but read backwards.
 *
 * FINISHED ROWS SORT AS BLANK (null), so they sit at the bottom whichever way
 * the arrow points. One click then ranks the work that is actually still
 * slipping — which is the only reason to sort this column. Mixing a task
 * delivered two days late in among live items 40 days overdue would bury the
 * thing the reader is looking for. The delivered figure is still on screen in
 * the cell for anyone reading rather than ranking.
 */
export function slippageSortValue({ eta, revised, done }) {
  if (done) return null;
  return daysLeft(revised, eta);
}

/*
  The schedule filter.

  "overdue_revised" is the question that prompted all of this: which things
  went past the date they originally promised AND have a new date recorded
  against them. It is measured against `eta`, NOT against the revised date —
  something re-committed to next month is still something that missed its first
  promise, and that is exactly the population being counted. Measuring against
  NVL(revised, eta) would make every one of them vanish from the list the moment
  somebody moved the date, which is the opposite of what it is for.

  Every "is it late" option excludes FINISHED work. Without that, "past its
  target date" returned 161 tasks of which 127 were already delivered — a
  number nobody can act on. "Delivered late" is the option for looking
  backwards at those deliberately.
*/
export const SCHEDULE_FILTERS = [
  { id: "all", label: "Any schedule state" },
  { id: "overdue_revised", label: "Past ETA + revised set" },
  { id: "overdue", label: "Overdue (still open)" },
  { id: "revised", label: "Has a revised ETA" },
  { id: "on_track", label: "On or ahead of target" },
  { id: "delivered_late", label: "Delivered late" },
  { id: "no_target", label: "No target date" },
];

export function matchesSchedule(id, { eta, revised, done = false, completedAt = null }) {
  if (!id || id === "all") return true;
  const age = ageingDays(revised, eta);
  switch (id) {
    case "overdue_revised":
      return !done && Boolean(revised) && (ageingAgainstOriginal(eta) ?? -1) > 0;
    case "overdue":
      return !done && age !== null && age > 0;
    case "revised":
      return Boolean(revised);
    case "on_track":
      return !done && age !== null && age <= 0;
    case "delivered_late": {
      if (!done) return false;
      const v = deliveredVariance(completedAt, revised, eta);
      return v !== null && v > 0;
    }
    case "no_target":
      return age === null;
    default:
      return true;
  }
}
